'use client';

import { useEffect, useState } from 'react';
import { Box, Select, MenuItem, FormControl, Button, TextField, Snackbar,
         IconButton, Tooltip, InputAdornment } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sessionActions } from '../store';
import { useLocalization, useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import usePersistedState from '../common/util/usePersistedState';
import {
  generateLoginToken, handleLoginTokenListeners, nativeEnvironment, nativePostMessage,
} from '../common/components/NativeInterface';
import LogoImage from './LogoImage';
import { useCatch } from '../reactHelper';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import QrCodeDialog from '../common/components/QrCodeDialog';
import fetchOrThrow from '../common/util/fetchOrThrow';
import PlayStoreImage from '../resources/images/custom/playstore.svg';
import AppStoreImage from '../resources/images/custom/applestore.svg';

const useStyles = makeStyles()((theme) => ({
  options: {
    position: 'fixed',
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.8),
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(0, 0, 0, 0.5)"
      : "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(12px)",
    padding: theme.spacing(3),
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
    maxWidth: "460px",
    width: "100%",
    position: "relative",
    zIndex: 1,
    animation: "fadeIn 0.6s ease-out",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    "@keyframes fadeIn": {
      "0%": { opacity: 0, transform: "translateY(20px)" },
      "100%": { opacity: 1, transform: "translateY(0)" },
    },
    [theme.breakpoints.down('sm')]: {
      margin: 'auto',
      maxHeight: '90vh',
      overflowY: 'auto',
      padding: theme.spacing(2.5),
      gap: theme.spacing(1.2),
      maxWidth: "95%",
    },
  },
  extraContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: theme.spacing(1.8),
    marginTop: theme.spacing(0.8),
    width: "100%",
  },
  registerButton: {
    minWidth: '150px',
    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.primary.main,
    borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.23)",
    padding: "6px 12px",
    fontSize: "0.9rem",
    '&:hover': {
      backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
      borderColor: theme.palette.mode === "dark" ? "#fff" : theme.palette.primary.main,
    }
  },
  resetPassword: {
    minWidth: '150px',
    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.primary.main,
    borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.23)",
    padding: "6px 12px",
    fontSize: "0.9rem",
    '&:hover': {
      backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
      borderColor: theme.palette.mode === "dark" ? "#fff" : theme.palette.primary.main,
    }
  },
  appLinksContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(0.5),
    width: '100%',
  },
  appLink: {
    height: '50px',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    boxSizing: "border-box",
    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
    borderRadius: "8px",
    marginBottom: theme.spacing(1.5),
    background: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
    "&:focus-within": {
      background: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(0, 0, 0, 0.08)",
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
    fontSize: "1rem",
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    '& img': {
      width: '180px',
      height: 'auto',
      [theme.breakpoints.down('sm')]: {
        width: '140px',
      },
    },
  },
  loginButton: {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "white",
    fontWeight: "bold",
    padding: "14px 0",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    fontSize: "1rem",
    minHeight: "44px",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
    },
    transition: "all 0.2s ease",
  },
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5),
    },
  },
}));

const LoginPage = () => {
  const revenda = useSelector((state) => state.session.revenda);
  const serverAttributes = useSelector((state) => state.session.server.attributes);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const t = useTranslation();

  const { languages, language, setLocalLanguage } = useLocalization();
  const languageList = Object.entries(languages).map((values) => ({ code: values[0], country: values[1].country, name: values[1].name }));

  const [failed, setFailed] = useState(false);
  const [email, setEmail] = usePersistedState('loginEmail', '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showServerTooltip, setShowServerTooltip] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const registrationEnabled = useSelector((state) => state.session.server.registration);
  const languageEnabled = useSelector((state) => {
    const attributes = state.session.server.attributes;
    return !attributes.language && !attributes['ui.disableLoginLanguage'];
  });
  const changeEnabled = useSelector((state) => !state.session.server.attributes.disableChange);
  const emailEnabled = useSelector((state) => state.session.server.emailEnabled);
  const openIdEnabled = useSelector((state) => state.session.server.openIdEnabled);
  const openIdForced = useSelector((state) => state.session.server.openIdEnabled && state.session.server.openIdForce);
  const [codeEnabled, setCodeEnabled] = useState(false);

  const [announcementShown, setAnnouncementShown] = useState(false);
  const announcement = useSelector((state) => state.session.server.announcement);

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    setFailed(false);
    try {
      const query = `email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;
      const response = await fetch('/api/session', {
        method: 'POST',
        body: new URLSearchParams(code.length ? `${query}&code=${code}` : query),
      });
      if (response.ok) {
        const user = await response.json();
        generateLoginToken();
        dispatch(sessionActions.updateUser(user));
        const target = window.sessionStorage.getItem('postLogin') || '/';
        window.sessionStorage.removeItem('postLogin');
        navigate(target, { replace: true });
      } else if (response.status === 401 && response.headers.get('WWW-Authenticate') === 'TOTP') {
        setCodeEnabled(true);
      } else {
        throw Error(await response.text());
      }
    } catch {
      setFailed(true);
      setPassword('');
    }
  };

  const handleTokenLogin = useCatch(async (token) => {
    const response = await fetchOrThrow(`/api/session?token=${encodeURIComponent(token)}`);
    const user = await response.json();
    dispatch(sessionActions.updateUser(user));
    navigate('/');
  });

  const handleOpenIdLogin = () => {
    document.location = '/api/session/openid/auth';
  };

  useEffect(() => nativePostMessage('authentication'), []);

  useEffect(() => {
    const listener = (token) => handleTokenLogin(token);
    handleLoginTokenListeners.add(listener);
    return () => handleLoginTokenListeners.delete(listener);
  }, []);

  useEffect(() => {
    if (window.localStorage.getItem('hostname') !== window.location.hostname) {
      window.localStorage.setItem('hostname', window.location.hostname);
      setShowServerTooltip(true);
    }
  }, []);

  const commonInputStyles = {
    "& .MuiInputBase-root": {
      color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.42)"
        : "rgba(0, 0, 0, 0.42)",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.87)"
        : "rgba(0, 0, 0, 0.87)",
    },
    "& .MuiInputAdornment-root": {
      color: theme.palette.mode === "dark"
        ? "#fff"
        : theme.palette.text.primary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.primary.main,
      transform: "scaleX(1)",
      transition: "transform 0.3s, border-color 0.3s",
    },
  };

  return (
    <LoginLayout>
      <div className={classes.options}>
        {(nativeEnvironment || window.location.hostname !== 'localhost') && changeEnabled && (
          <IconButton color="primary" onClick={() => navigate('/change-server')}>
            <Tooltip
              title={`${t('settingsServer')}: ${window.location.hostname}`}
              open={showServerTooltip}
              arrow
            >
              <VpnLockIcon />
            </Tooltip>
          </IconButton>
        )}
        {!nativeEnvironment && (
          <IconButton color="primary" onClick={() => setShowQr(true)}>
            <QrCode2Icon />
          </IconButton>
        )}
        {languageEnabled && (
          <FormControl>
            <Select
              value={language}
              onChange={(e) => setLocalLanguage(e.target.value)}
              sx={{
                bgcolor: theme.palette.mode === "dark" ? theme.palette.background.paper : "rgba(255, 255, 255, 0.9)",
                color: theme.palette.text.primary,
                "& .MuiSelect-select": {
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  gap: theme.spacing(1),
                },
              }}
            >
              {languageList.map((it) => (
                <MenuItem key={it.code} value={it.code}>
                  <Box component="span" sx={{ mr: 1 }}>
                    <ReactCountryFlag countryCode={it.country} svg />
                  </Box>
                  {it.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
      <div className={classes.wrapper}>
        <div className={classes.container}>
          <LogoImage />
          {!openIdForced ? (
            <>
              <TextField
                required
                error={failed}
                placeholder={t("userEmail")}
                name="email"
                value={email}
                autoComplete="email"
                autoFocus={!email}
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                  className: classes.input,
                  disableUnderline: true,
                }}
                onChange={(e) => setEmail(e.target.value)}
                helperText={failed && "Usuário ou Senha Inválidos"}
                sx={{
                  ...commonInputStyles,
                  "& .MuiFormHelperText-root": {
                    color: theme.palette.error.main,
                  },
                }}
              />
              <TextField
                required
                error={failed}
                placeholder={t("userPassword")}
                name="password"
                value={password}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                autoFocus={!!email}
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  className: classes.input,
                  disableUnderline: true,
                }}
                onChange={(e) => setPassword(e.target.value)}
                sx={commonInputStyles}
              />
              {codeEnabled && (
                <TextField
                  required
                  error={failed}
                  placeholder={t("loginTotpCode")}
                  name="code"
                  value={code}
                  type="number"
                  variant="standard"
                  onChange={(e) => setCode(e.target.value)}
                  sx={{
                    "& .MuiInputBase-root": {
                      color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
                    },
                    "& .MuiInput-underline:before": {
                      borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.42)" : "rgba(0, 0, 0, 0.42)",
                    },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                      borderBottomColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
                    },
                  }}
                />
              )}
              <Button
                onClick={handlePasswordLogin}
                type="submit"
                variant="contained"
                color="primary"
                disabled={!email || !password || (codeEnabled && !code)}
                className={classes.loginButton}
              >
                {t('loginLogin')}
              </Button>
              {openIdEnabled && (
                <Button
                  onClick={() => handleOpenIdLogin()}
                  variant="outlined"
                  sx={{
                    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
                    borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                  }}
                >
                  {t('loginOpenId')}
                </Button>
              )}
              <div className={classes.extraContainer}>
                {registrationEnabled && (
                  <Button
                    onClick={() => navigate('/register')}
                    variant="outlined"
                    size="small"
                    className={classes.registerButton}
                  >
                    {t('loginRegister')}
                  </Button>
                )}
                {emailEnabled && (
                  <Button
                    onClick={() => navigate('/reset-password')}
                    variant="outlined"
                    size="small"
                    className={classes.resetPassword}
                  >
                    {t('loginReset')}
                  </Button>
                )}
              </div>

              {(revenda?.googleplay || revenda?.applestore || serverAttributes?.androidAppLink || serverAttributes?.iosAppLink) && (
                <Box className={classes.appLinksContainer}>
                  {revenda?.googleplay || serverAttributes?.androidAppLink ? (
                    <a
                      href={revenda?.googleplay || serverAttributes?.androidAppLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={PlayStoreImage}
                        alt="Google Play"
                        className={classes.appLink}
                      />
                    </a>
                  ) : null}

                  {revenda?.applestore || serverAttributes?.iosAppLink ? (
                    <a
                      href={revenda?.applestore || serverAttributes?.iosAppLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={AppStoreImage}
                        alt="App Store"
                        className={classes.appLink}
                      />
                    </a>
                  ) : null}
                </Box>
              )}
            </>
          ) : (
            <Button
              onClick={() => handleOpenIdLogin()}
              variant="contained"
              color="primary"
              className={classes.loginButton}
            >
              {t('loginOpenId')}
            </Button>
          )}
        </div>
      </div>
      <QrCodeDialog open={showQr} onClose={() => setShowQr(false)} />
      <Snackbar
        open={!!announcement && !announcementShown}
        message={announcement}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setAnnouncementShown(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default LoginPage;